<template>
  <span class="screen-permission-gate">
    <slot v-if="allowed" />
    <slot v-else name="fallback" />
  </span>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useStore } from 'vuex';
  import { hasScreenCodePermission } from '@/libs/auth';
  import type { PermissionActionKey } from '@/types/auth';

  const props = withDefaults(
    defineProps<{
      /** Permission tree node code (e.g. ACCEPT, ACCEPT-BTN_EDIT). */
      code: string | string[];
      /** Minimum action required (default VIEW). */
      action?: PermissionActionKey;
    }>(),
    {
      action: 'VIEW'
    }
  );

  const store = useStore();
  const allowed = ref(false);

  const refresh = () => {
    const codes = Array.isArray(props.code) ? props.code : [props.code];
    allowed.value = codes.some((code) =>
      hasScreenCodePermission(code, props.action)
    );
  };

  watch(
    () => [props.code, props.action] as const,
    () => refresh(),
    { immediate: true }
  );

  let stopWatch: (() => void) | undefined;

  onMounted(() => {
    stopWatch = store.watch(
      (_state, getters) => getters['auth/auth'],
      () => refresh(),
      { deep: true }
    );
  });

  onBeforeUnmount(() => {
    stopWatch?.();
  });
</script>

<style scoped>
  .screen-permission-gate {
    display: inline;
  }
</style>
